import moment from 'moment'
import { buildLegacySessionFromAmplify, COGNITO_STORAGE_PREFIX } from '@/globals/cognito-compat'
import EventBus from '@/components/mixins/event-bus-mixin'
import TemplateMixin from '@/components/mixins/template-mixin'
import Utils, { Dependencies } from '@/globals/utils.js'
import { useZendeskMessenger } from '@base-shared/composables/use-zendesk-messenger'
import {
  signOut as AmplifySignOut
} from 'aws-amplify/auth'
import { Amplify } from 'aws-amplify';

import { mapActions } from 'pinia'
import { useMediaListStore } from '@/pinia/stores/media/mediaListStore'
import { useMediaQueryStore } from '@/pinia/stores/media/mediaQueryStore'
import { useMediaStore } from '@/pinia/stores/media/mediaStore'
import { useMediaSelectionStore } from '@/pinia/stores/media/mediaSelectionStore'
import { useFileUploadProgressStore } from '@/pinia/stores/file-upload/fileUploadProgressStore'
import { useLocalStorageStore } from '@base-shared/stores/local-storage'
import { useCognitoAuthStore } from '@base-shared/stores/cognito-auth'
import { useUserStore } from '@base-shared/stores/user'
import { useGroupStore } from '@base-shared/stores/group'
import { t } from '@/i18n/i18ns'

const LEGACY_LOCAL_STORAGE_USER_SESSION = 'aryaUserSession';

/**
 * Clear legacy user session from localStorage if it exists.
 * This is used to remove deprecated session data that may
 * have been stored by older versions of the application.
 */
function clearLegacyLocalStorageUserSession () {
  if (!localStorage.getItem(LEGACY_LOCAL_STORAGE_USER_SESSION)) return;
  localStorage.removeItem(LEGACY_LOCAL_STORAGE_USER_SESSION);
}

export default {
  mixins: [TemplateMixin],
  inject: [
    '$currentUser',
    '$utils',
    '$dataStore',
    '$api',
    '$apiError',
    '$signedInUserDetails',
    '$filtersRepository'
  ],
  methods: {
    ...mapActions(useMediaListStore, {
      resetMediaListStore: 'reset'
    }),
    ...mapActions(useMediaQueryStore, {
      resetMediaQueryStore: 'reset'
    }),
    ...mapActions(useMediaStore, {
      resetMediaStore: 'reset'
    }),
    ...mapActions(useMediaSelectionStore, {
      resetMediaSelectionStore: 'reset'
    }),
    ...mapActions(useFileUploadProgressStore, {
      resetFileUploadProgressStore: 'reset'
    }),
    ...mapActions(useLocalStorageStore, {
      resetLocalStorage: 'resetLocalStorage'
    }),

    resetPiniaStores () {
      this.resetMediaListStore()
      this.resetMediaQueryStore()
      this.resetMediaStore()
      this.resetMediaSelectionStore()
      this.resetFileUploadProgressStore()
      this.resetLocalStorage()
    },
    async fetchAuthSession () {
      return await useCognitoAuthStore().fetchAndStoreAuthSession()
    },
    sendSignedInEvent (params) {
      if (!params) params = { mainNavVisible: true }
      EventBus.$emit('SIGNED_IN', params)
    },
    sendUserSessionUpdatedEvent () {
      EventBus.$emit('USER_SESSION_UPDATED')
    },
    sendSignedOutEvent (params) {
      if (!params) params = { mainNavVisible: true }
      EventBus.$emit('SIGNED_OUT', { mainNavVisible: false })
    },
    getUserDetailsFromBackend (params = {}) {
      useUserStore().getCurrentUserDetails()
        .then(({ error, response }) => {
          if (error) {
            this.apiError().handleAPIRequestError(error?.cause ?? error, {
              originalRequest: () => this.getUserDetailsFromBackend(params),
              showDialogNotification: true
            });

            return;
          }

          const currentUser = this.utils().clone(response?.currentUser);
          this.handleUserInfoInChatWindow('setUserInfo');
          if (params.callback) params.callback(currentUser);
        })
    },
    getUserAccountsFromBackend (params = {}) {
      useUserStore().getAccounts()
        .then(({ error, response }) => {
          if (error) {
            this.apiError().handleAPIRequestError(error?.cause ?? error, {
              originalRequest: () => this.getUserAccountsFromBackend(params),
              showDialogNotification: true
            });

            return;
          }

          const userAccounts = this.utils().clone(response?.accounts);
          if (params.callback) params.callback(userAccounts);
        });
    },
    getUserUserInfoFromBackend (params = {}) {
      useGroupStore().loadGroupInfo()
        .then(({ error, response }) => {
          if (error) {
            this.apiError().handleAPIRequestError(error?.cause ?? error, {
              originalRequest: () => this.getUserUserInfoFromBackend(params),
              showDialogNotification: true
            });

            return;
          }

          const userInfo = this.currentUser().filterUserInfoFromBackend(response?.groupInfo);
          if (params.callback) params.callback(userInfo);
        });
    },
    /**
     * Force-update UserSession both CognitoUser details and
     * User, Account and UserInfo update
     * @param {*} signInUserSession - cognito UserSession as an input
     */
    async updateUserSession () {
      this.sendUserSessionUpdatedEvent()

      // TODO: refactor to async
      this.getUserDetailsFromBackend()
      this.getUserAccountsFromBackend()
      this.getUserUserInfoFromBackend()
    },
    getURLQueryString () {
      let queryString = ''

      if (this.$route.query) {
        for (const rQIndx in this.$route.query) {
          // this.$route.query values are already decoded by Vue Router, so they
          // must be re-encoded before being embedded in a URL string. Without
          // this, characters like `#` (decoded from `%23`) would be treated as
          // a fragment separator, and bare `%` signs (e.g. from a search term
          // like "50%") would form invalid percent-sequences that cause a
          // Vue Router "Error decoding" warning on the next navigation.
          const val = this.$route.query[rQIndx]
          const encodedVal = Array.isArray(val)
            ? val.map(v => encodeURIComponent(v ?? '')).join(',')
            : encodeURIComponent(val ?? '')
          const qVal = `${encodeURIComponent(rQIndx)}=${encodedVal}`

          if (queryString === '') {
            queryString += `${qVal}`
          } else {
            queryString += `&${qVal}`
          }
        }
      }

      if (queryString.length > 0) {
        return `?${queryString}`
      }

      return ''
    },
    isAuthenticated (params) {
      if (!params || ((typeof params) !== 'object')) params = {}

      const handleUnauthenticated = async () => {
        if (params.targetPageAfterLogin) localStorage.setItem('targetPageAfterLogin', params.targetPageAfterLogin)
        this.handleUserInfoInChatWindow('clear') // Clear chat window
        if (this.getTermsOfServicesStatus().visible === true) localStorage.removeItem('aryaShowTOS') // Clear TOS
        this.removeTemplateSetSelectionFromLocalStorage() // Remove template selection
        await this.clearAllCognitoDataInLocalstorage()
        this.resetPiniaStores() // Reset related pinia stores
        this.sendSignedOutEvent()
        this.handlePublicPages()
      }

      useCognitoAuthStore().fetchAndStoreAuthSession()
        .then(async (session) => {
          if (session?.tokens?.accessToken && session?.tokens?.idToken) {
            this.sendSignedInEvent()

            // Get user details from backend and bind into Vue
            if (!this.signedInUserDetails()) {
              this.getUserDetailsFromBackend()
              this.getUserAccountsFromBackend()
              this.getUserUserInfoFromBackend()
            }

            const $router = Dependencies.inject('$routerInstance')
            // Redirect to Terms and Services
            if ((this.getTermsOfServicesStatus().visible === true) && (this.getTermsOfServicesStatus().type === 'required')) {
              $router.push('/tos', () => {})

              return
            }

            // Workaround for S3 to redirect into specific route
            const currentUrl = (this.$route.hash.substring(1) + this.getURLQueryString()).toString()
            let redirectUrl = currentUrl
            const isSignInInviteForm = this.$route.query?.['lform'] === 'explicit';
            const isSignInPage = (this.$route.name === 'signIn') && (currentUrl.length === 0 || currentUrl === '/' || isSignInInviteForm)
            if (isSignInPage) redirectUrl = '/loginok'
            $router.push(redirectUrl)
            // The CDN redirects SPA paths via the hash (e.g. /#/media?foo=bar).
            // filtersRepository.init() ran at app boot when window.location.search
            // is empty (query params were inside the hash fragment) up until the
            // hash is removed and Vue Router has already completed navigation and
            // window.location.search returns the correct value. Re-init here so
            // subsequent filtersRepository.findXxx() calls (e.g. mediaQuery.initialize())
            // read the correct URL params. Intended to not await if the $router.push
            // succeeded since this is only needed to kickstart ability to read from
            // URL params, not the final filters state.
            this.$filtersRepository.init(this.$dataStore)

            // Process callback
            if (params.callback) params.callback()
          } else {
            await handleUnauthenticated();
          }
        })
        .catch(async (error) => {
          console.log('Error in isAuthenticated: ', error)
          await handleUnauthenticated();
        })
    },
    /**
     * Handle window chat user details
     * @param {String} actionType
     */
    handleUserInfoInChatWindow (actionType) {
      if (actionType === 'setUserInfo') {
        // TODO: Temporarily disable logging user in zE
        // zE('webWidget', 'identify', {
        //   name: this.signedInUserDetails().name,
        //   email: this.signedInUserDetails().email
        // })
        useZendeskMessenger().setLocale(this.dataStore().getLanguage().code)
      }

      // TODO: Since logging user in is temporarily disabled,
      // disable logging user out so it can retain its session
      // if (actionType === 'clear') {
      //   zE('messenger', 'logoutUser')
      // }
    },
    /**
     * Manually clear all Cognito-related data from localStorage,
     * including tokens and user session information.
     */
    async clearAllCognitoDataInLocalstorage () {
      // Clear legacy 'aryaUserSession' from localStorage
      // if it exists.
      clearLegacyLocalStorageUserSession();

      await useCognitoAuthStore().fetchAndStoreAuthSession()
      if (!useCognitoAuthStore().isAuthenticated) return;

      useCognitoAuthStore().configureAmplify();
      await AmplifySignOut({ global: false }).catch((err) => {
        console.warn('Amplify signOut error (non-fatal):', err);
      });

      // Remove all Cognito localStorage entries to ensure complete cleanup
      // across all configured stages/pools used during this session.
      for (const key of Object.keys(localStorage)) {
        if (key.startsWith(COGNITO_STORAGE_PREFIX)) {
          localStorage.removeItem(key);
        }
      }
    },
    /**
     * Revoke Refresh Token for native or external login
     * @param {Object} params
     *  - params.callback - callback function to execute after revoking
     */
    async signOutFromAmplify (params) {
      if (!params) params = {}
      const provider = useCognitoAuthStore().getProviderInfoAndCognitoStage()?.SSOProviderName

      if (provider) { // external login
        const redirectUrl = Amplify.getConfig().Auth?.Cognito.loginWith?.oauth?.redirectSignOut[0] ?? `${location.protocol}//${location.host}/`;
        await AmplifySignOut({ global: false, oauth: { redirectUrl } })
      } else { // native login
        await AmplifySignOut()
      }
      // Update the auth session after signing out
      await useCognitoAuthStore().fetchAndStoreAuthSession()

      if (params.callback) params.callback()
    },
    async signOut (params) {
      if (!params) params = {}

      await useCognitoAuthStore().fetchAndStoreAuthSession()
      const provider = useCognitoAuthStore().getProviderInfoAndCognitoStage()?.SSOProviderName

      this.handleUserInfoInChatWindow('clear') // Clear chat window

      if (this.getTermsOfServicesStatus().visible === true) localStorage.removeItem('aryaShowTOS') // Clear TOS

      this.removeTemplateSetSelectionFromLocalStorage() // Remove template selection

      if (useCognitoAuthStore().isAuthenticated) {
        this.utils().mainLoader(true)
        this.signOutFromAmplify({
          callback: async (response) => {
            await this.clearAllCognitoDataInLocalstorage()
            this.resetPiniaStores() // Reset related pinia stores
            this.sendSignedOutEvent()
            this.utils().mainLoader(false)

            if (!provider) { // native login
              const $router = Dependencies.inject('$routerInstance')
              if (params.sso_migrate_account) {
                $router.push(`/?sso_migrate_account=${this.utils().encodeBase64Format(params.sso_migrate_account)}`, () => {})
              } else {
                $router.push('/', () => {})
              }
            }
          }
        })
      } else {
        this.handlePublicPages()
      }
    },
    handlePublicPages () {
      // Workaround for S3 to redirect to public pages

      const publicPaths = ['/cookie-notice', '/privacy', '/sso', '/logoutok']
      let currentPath = window.location.pathname
      let redirectUrl = '/'
      const URLQueryString = this.getURLQueryString()

      if (window.location.hash) {
        currentPath = this.$route.hash.substring(1) + URLQueryString
      }

      // Public pages
      if (publicPaths.indexOf(currentPath) >= 0 || publicPaths.find((p) => { return currentPath.includes(p) })) {
        redirectUrl = URLQueryString ? currentPath + URLQueryString : currentPath
      } else if (currentPath === '/' && URLQueryString) {
        redirectUrl = currentPath + URLQueryString
      }

      const $router = Dependencies.inject('$routerInstance')
      $router.push(redirectUrl, () => {})
    },
    /**
     * Check Terms and Services
     * @param {Object} - params
     */
    async checkTermsOfServices (params) {
      const session = await useCognitoAuthStore().fetchAndStoreAuthSession()
      const getTOSAcceptDate = session?.tokens?.idToken?.payload?.['custom:tos_accept_date']
      const setToLocStorage = (details) => {
        if (localStorage.getItem('aryaShowTOS') !== null) {
          localStorage.removeItem('aryaShowTOS')
        }

        localStorage.setItem('aryaShowTOS', details)
      }

      // If No acceptance date, then show Terms of Services
      if (!getTOSAcceptDate) {
        setToLocStorage('required')
        this.$router.push('/tos', () => {})
      }

      // If there's an acceptance date and acceptance date is before the current TOS
      const currentTOSDate = moment('2019-12-10')
      if (getTOSAcceptDate && moment(getTOSAcceptDate).isBefore(currentTOSDate)) {
        const TOSAcceptanceDate = moment(getTOSAcceptDate)
        const durationByMonth = moment.duration(TOSAcceptanceDate.diff(currentTOSDate)).months()

        // TOS Acceptance date is before the current TOS by more than one month
        if (durationByMonth < 0) {
          setToLocStorage('required')
          this.$router.push('/tos', () => {})
        }

        // TOS Acceptance date is before the current TOS by less than one month
        if (durationByMonth === 0) {
          setToLocStorage('optional')
          this.$router.push('/tos', () => {})
        }
      } else {
        params.callback()
      }
    },
    getTermsOfServicesStatus () {
      const res = { visible: false, type: 'optional' }
      const aryaShowTOS = localStorage.getItem('aryaShowTOS')

      if (aryaShowTOS !== null) {
        res.visible = true
        res.type = aryaShowTOS
      }

      return res
    },
    /**
     * Show failed refresh token notification
     * This is used when the refresh token is expired or invalid.
     * It will show a notification to the user and sign out the user.
     */
    showFailedRefreshToken () {
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'error',
        alert: {
          message: t('errors.failedRefreshToken')
        },
        callbacks: {
          eventOnClose: this.signOut
        }
      })
    },
    /**
     * Refresh Token
     * @param {*} params - there are few important ones:
     *   - params.nextAction - callback what would be next after refresh
     *   - params.hideLoader - to not show loader while refreshing
     * @returns
     */
    async refreshUserSession (params) {
      const shouldShowLoader = !params?.hideLoader
      if (shouldShowLoader) {
        this.utils().mainLoader(true)
      }

      await useCognitoAuthStore().fetchAndStoreAuthSession()
      if (!useCognitoAuthStore().isAuthenticated) {
        if (shouldShowLoader) this.utils().mainLoader(false)
        this.showFailedRefreshToken()
        return
      }

      try {
        useCognitoAuthStore().configureAmplify();
        await useCognitoAuthStore().forceRefreshToken();
        await useCognitoAuthStore().fetchAndStoreAuthSession()

        if (!useCognitoAuthStore().isAuthenticated) {
          this.showFailedRefreshToken()
          return
        }

        await this.updateUserSession()

        if (params?.nextAction) {
          params.nextAction()
        }
      } catch (error) {
        console.log('Failed to refresh user session: ', error)
        this.showFailedRefreshToken()
        return
      } finally {
        if (shouldShowLoader) {
          this.utils().mainLoader(false)
        }
      }
    },

    /**
     * Revoke Refresh Token for native login
     * This is used for native email and password login.
     * @param {*} params
     * - params.callback - callback function to execute after revoking
     */
    async postRevokeRefreshTokenFromLegacySession (params) {
      if (!params) params = {}

      const providerInfoAndCognitoStage = useCognitoAuthStore().getProviderInfoAndCognitoStage();
      const legacySession = await buildLegacySessionFromAmplify(undefined, providerInfoAndCognitoStage)
      if (!legacySession?.accessToken || !legacySession?.idToken) {
        if (params.callback) params.callback()
        return
      }

      const stage = providerInfoAndCognitoStage?.CognitoStage
      const pool = CONFIG.AWS_COGNITO_USER_POOLS[stage]
      if (!pool) {
        this.error = new Error(`No user pool found for stage: ${stage}`)
        this.utils().mainLoader(false)
        return
      }

      const { success, error, response } = await useCognitoAuthStore().postCognitoOAuthRevoke(pool.domain, pool.clientId, legacySession.refreshToken.token)
      if (!success || error) {
        this.utils().mainLoader(false)
        return
      }

      if (params.callback) params.callback(response)
    },
    utils () {
      if (this.$utils) return this.$utils
      return Utils
    },
    api () {
      return this.$api
    },
    apiError () {
      return this.$apiError
    },
    signedInUserDetails () {
      return this.$signedInUserDetails
    },
    dataStore () {
      return this.$dataStore
    },
    currentUser () {
      return this.$currentUser
    }
  }
}
